import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { PermissionService } from '@app/services/permission.service';
import { PlatformService } from '@app/services/platform.service';
import { Router } from '@angular/router';
import { BaseConfigService, FamilyService, FormConfigService, PreinscriptionService } from '@app/services';
import { filter, takeUntil, tap, switchMap, map } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { PreInscription } from '@app/models/pre-inscription';
import { BoardCard } from '@app/models/board-card';
import { MikadoPreInscriptionOptionConfig } from '@app/models/mikado';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app/components/_common/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Family } from '@app/models/family';

@Component({
  selector: 'app-user-preinscriptions',
  templateUrl: './preinscription-list.component.html',
  styleUrls: ['./preinscription-list.component.scss'],
  standalone: false
})
export class PreinscriptionListComponent implements OnInit, OnDestroy {

  preinscriptions: PreInscription[];
  config: MikadoPreInscriptionOptionConfig;
  formParts: any[];
  readOnly: boolean;
  permToCreate: boolean;
  addBtnUrl: string;
  loading = false;
  onDestroy$ = new Subject<void>();

  constructor(
    private familyService: FamilyService,
    public platformService: PlatformService,
    public permService: PermissionService,
    private formConfigService: FormConfigService,
    public router: Router,
    private baseConfigService: BaseConfigService,
    private preinscriptionService: PreinscriptionService,
    private dialog: MatDialog,
    private translator: TranslateService
  ) { }

  ngOnInit() {
    this.readOnly = !this.permService.hasPermission('preinscription_edit') || !this.familyService.currentFamily.active;
    this.permToCreate = this.permService.hasPermission('preinscription_create') && this.familyService.currentFamily.active;
    this.loadData().subscribe(({ steps, config }) => {
      this.formParts = steps;
      // `this.config` est déjà mis à jour dans le `tap()`
    });

    this.baseConfigService.getFirstConf$("board-user")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(conf => {
        const cards = conf.content.cards;
        const idDemarche = cards.find((card: BoardCard) => card.component === 'preinscription').addBtnDemarcheId;
        this.addBtnUrl = idDemarche ? `add/${idDemarche}` : 'add';
      })

    this.loadPreinscriptions()
  }

  loadPreinscriptions() {
    this.familyService.currentFamily$.pipe(
      filter(f => !!f),
      takeUntil(this.onDestroy$),
      tap(_ => { this.preinscriptions = null; this.loading = false; }),
      switchMap(f => this.getPreinscriptionsByFamily(f))
    ).subscribe();
  }

  loadData() {
    const filteredSteps = ['InformationsImportantes'];

    return forkJoin({
      steps: this.formConfigService.getForm('form-pre-inscription').pipe(
        map(steps => steps
          .filter(step => !filteredSteps.includes(step.stepName))
          .map(step => ({ stepName: step.stepName, label: step.label }))
        )
      ),
      config: this.preinscriptionService.getConfigOptionsPreinscription()
    }).pipe(
      tap(({ config }) => {
        this.config = config;
      })
    );
  }

  getPreinscriptionsByFamily(f: Family) {
    return this.familyService.getFamilyPreInscriptions(f, "Mikado")
      .pipe(tap((data: PreInscription[]) => {
        this.preinscriptions = data;
        this.preinscriptions.forEach(preinsc => {
          preinsc.programmation = Object.entries(preinsc.programmation ?? {})
        })
      }))
  }

  onConfirmButton(preinsc: PreInscription) {
    this.translator.instant('mikado.preinscriptions.text_dialog_confirmation');
    const ref = this.dialog.open(ConfirmDialogComponent, { data: { message: this.translator.instant('mikado.preinscriptions.text_dialog_confirmation'), title: this.translator.instant('mikado.preinscriptions.title_dialog_confirmation'), confirmText: 'Oui', cancelText: 'Non' } });
    ref.afterClosed().subscribe(result => {
      if (result) {
        preinsc._isLoaded = true;
        this.preinscriptionService.updateDataConfirmation(preinsc.idPreInscription).subscribe(_ => {
          this.familyService.currentFamily$.pipe(
            filter(f => !!f),
            takeUntil(this.onDestroy$),
            switchMap(f => this.getPreinscriptionsByFamily(f))
          ).subscribe();
        });
      }
    });
  }

  openForm(PreInscriptionId, stepName) {
    this.router.navigate([`/account/preinscription/edit/${PreInscriptionId}/${stepName}`]);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
